import { useState, useEffect } from "react";
import { FaPrint, FaEdit } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { HiXMark } from "react-icons/hi2";
import { format } from "date-fns";
import HospitalBillModal from "./HospitalBillModal";
import { useAuth } from "../../../utils/AuthContext";
import { PrintBillLayoutA5 } from "../../PrintLayoutA5";

const HospitalViewBillModal = ({ isOpen, onClose, bill: initialBill, onEdit, onDelete }) => {
  const [bill, setBill] = useState(initialBill);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { user } = useAuth();

  // Sync local bill state with prop changes
  useEffect(() => {
    setBill(initialBill);
  }, [initialBill]);

  if (!isOpen || !bill) return null;

  const {
    id,
    billNumber,
    createdAt,
    billingDate,
    patientName,
    specialNo,
    regNo,
    refer,
    referAmount,
    discount = 0,
    netAmount = 0,
    paidAmount = 0,
    payableAmount = 0,
    userName,
    serviceItems = [],
    admissionDate,
    authorizedBy,
    consultantName
  } = bill;

  // Normalize serviceItems to services
  const serviceList = serviceItems.length > 0
    ? serviceItems.map((service, index) => ({
        id: service.id || `service-${index}`,
        name: service.serviceName || "N/A",
        quantity: service.totalUnit || 0,
        price: service.unitPrice || 0,
        amount: service.amount || 0,
      }))
    : [];

  // Calculate sub-total
  const subTotal = serviceList.reduce((sum, service) => sum + (service.amount || service.quantity * service.price), 0);

  // Calculate due amount
  const dueAmount = parseFloat(payableAmount || (netAmount - paidAmount)).toFixed(2);

  // Handle print
  const handlePrintBill = () => {
    const elementsToHide = document.querySelectorAll(".print-hidden");
    elementsToHide.forEach((el) => (el.style.display = "none"));

    const printLayout = document.querySelector(".print-layout");
    if (printLayout) {
      printLayout.style.display = "block";
    }
    window.print();
    setTimeout(() => {
      elementsToHide.forEach((el) => (el.style.display = ""));
      if (printLayout) {
        printLayout.style.display = "none";
      }
    }, 500);
  };

  // Handle edit
  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  // Handle delete
  const handleDelete = () => {
    onDelete([id]);
    onClose();
  };
// Signature footer content for printing

 const signatureFooter = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0px', paddingRight: '6px' }}>
      <div style={{ textAlign: 'center', minWidth: '180px' }}>
        {/* অথোরাইজড ব্যক্তির নাম (স্বাক্ষর-সদৃশ) */}
        <p style={{ margin: '0 0 2px 0', fontWeight: 'bold', fontSize: '9.5pt', color: '#323290', fontFamily: 'cursive' }}>
          {authorizedBy || "N/A"}
        </p>

        {/* স্বাক্ষরের লাইন */}
        <div style={{ borderTop: '1.5px solid #323290', width: '160px', margin: '0 auto 3px auto' }}></div>

        {/* পদবী */}
        <p style={{ margin: '0', fontSize: '8pt', fontWeight: 'bold', letterSpacing: '0.5px', textTransform: 'uppercase', color: '#374151' }}>
          Authorized By
        </p>
        <p style={{ margin: '1px 0 0 0', fontSize: '7pt', color: '#6b7280' }}>
          Mukti Hospital Pvt. Ltd.
        </p>
      </div>
    </div>
  );
  // Define PrintLayout props
  const billInfoContent = (
    <>
      <div>
        <p><strong>Bill No:</strong> {billNumber || "N/A"}</p>
        <p><strong>Special No:</strong> {specialNo || "N/A"}</p>
        <p><strong>Reg No:</strong> {regNo || "N/A"}</p>
      </div>
      <div>
        <p><strong>Admission Date:</strong> {admissionDate ? new Date(admissionDate).toLocaleDateString("en-US") : "N/A"}</p>
        <p><strong>Billing Date:</strong> {billingDate ? format(new Date(billingDate), "MM/dd/yyyy") : createdAt ? format(new Date(createdAt), "MM/dd/yyyy") : "N/A"}</p>
        <p><strong>Bill Number:</strong> {billNumber || "N/A"}</p>
      </div>
      <p className="full"><strong>Patient Name:</strong> {patientName || "N/A"}</p>
      <p className="full"><strong>Consultant:</strong> {consultantName || "N/A"}</p>
    </>
  );

  const tableContent = (
    <>
      <thead>
        <tr>
          <th>Sl No</th>
          <th>Service Name</th>
          <th>Quantity</th>
          <th>Unit Price (৳)</th>
          <th>Amount (৳)</th>
        </tr>
      </thead>
      <tbody>
        {serviceList.length > 0 ? (
          serviceList.map((service, index) => (
            <tr key={service.id}>
              <td>{index + 1}</td>
              <td>{service.name}</td>
              <td>{service.quantity}</td>
              <td>{parseFloat(service.price).toFixed(2)}</td>
              <td>{parseFloat(service.amount).toFixed(2)}</td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan="5" style={{ textAlign: "center" }}>
              No services available
            </td>
          </tr>
        )}
      </tbody>
    </>
  );

  const summaryContent = (
    <>
      <div><span>Sub Total</span><span>{subTotal.toFixed(2)}</span></div>
      <div><span>Discount</span><span>{parseFloat(discount || 0).toFixed(2)}</span></div>
      <div><span>Net Total</span><span>{parseFloat(netAmount || 0).toFixed(2)}</span></div>
      <div><span>Paid Amount</span><span>{parseFloat(paidAmount || 0).toFixed(2)}</span></div>
      <div><span className="text-xl">Due Amount</span><span className="text-xl">{dueAmount}</span></div>
    </>
  );

  return (
    <>
      {/* Modal Content */}
      <div className="fixed inset-0 z-40 flex items-start sm:items-center justify-center bg-black bg-opacity-40 px-4 sm:px-0 overflow-y-auto print-hidden">
        <div className="bg-white shadow-lg w-full my-4 sm:my-0 max-h-[100vh] h-full overflow-y-auto">
          <div className="flex items-center justify-between gap-3 flex-wrap bg-gray-200 border-b border-dashed border-gray-300 p-4 print-hidden">
            <div className="flex items-center gap-3">
              <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                Bill Details
              </h2>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handlePrintBill}
                className="size-7 rounded-full bg-white text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                title="Print"
              >
                <FaPrint />
              </button>
              <button
                onClick={handleEdit}
                className="size-7 rounded-full bg-white text-M-Green-color text-sm inline-flex items-center justify-center hover:bg-M-Green-color hover:text-white transition-all duration-300"
                title="Edit"
              >
                <FaEdit />
              </button>
              <button
                onClick={handleDelete}
                className="size-7 rounded-full bg-white text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                title="Delete"
              >
                <RiDeleteBin6Line />
              </button>
              <button
                onClick={onClose}
                className="size-7 rounded-full bg-white text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                title="Close"
              >
                <HiXMark />
              </button>
            </div>
          </div>

          <div className="px-4 print-hidden">
            {/* Bill Info */}
            <div className="mt-4 sm:flex justify-between items-start mb-2">
              <div>
                <p className="text-black mb-1 text-[15px]">
                  <span className="w-[120px] inline-block">Bill No </span>:
                  <span className="text-gray-600 pl-1">{billNumber || "N/A"}</span>
                </p>
                <p className="text-black mb-1 text-[15px]">
                  <span className="w-[120px] inline-block">Patient Name </span>:
                  <span className="text-gray-600 pl-1">{patientName || "N/A"}</span>
                </p>
                <p className="text-black mb-1 text-[15px]">
                  <span className="w-[120px] inline-block">Special No </span>:
                  <span className="text-gray-600 pl-1">{specialNo || "N/A"}</span>
                </p>
                <p className="text-black mb-1 text-[15px]">
                  <span className="w-[120px] inline-block">Reg No </span>:
                  <span className="text-gray-600 pl-1">{regNo || "N/A"}</span>
                </p>
                <p className="text-black mb-1 text-[15px]">
                  <span className="w-[120px] inline-block">Consultant </span>:
                  <span className="text-gray-600 pl-1">{consultantName || "N/A"}</span>
                </p>
              </div>
              <div>
                <p className="text-black mb-1 text-[15px]">
                  <span className="w-[120px] inline-block">Billing Date </span>:
                  <span className="text-gray-600 pl-1">
                    {billingDate ? format(new Date(billingDate), "MM/dd/yyyy") : createdAt ? format(new Date(createdAt), "MM/dd/yyyy") : "N/A"}
                  </span>
                </p>
                <p className="text-black mb-1 text-[15px]">
                  <span className="w-[120px] inline-block">Refer </span>:
                  <span className="text-gray-600 pl-1">{refer || "N/A"}</span>
                </p>
                <p className="text-black mb-1 text-[15px]">
                  <span className="w-[120px] inline-block">Refer Amount </span>:
                  <span className="text-gray-600 pl-1">৳ {parseFloat(referAmount || 0).toFixed(2)}</span>
                </p>
                <p className="text-[15px] text-black mb-1">
                  <span className="w-[120px] inline-block">Submitted By </span>:
                  <span className="text-gray-600 pl-1">{user?.name || userName || "Unknown"}</span>
                </p>
                <p className="text-[15px] text-black mb-1">
                  <span className="w-[120px] inline-block">Authorized By </span>:
                  <span className="text-gray-600 pl-1">{authorizedBy || "N/A"}</span>
                </p>
              </div>
            </div>

            {/* Services Table */}
            {serviceList.length > 0 && (
              <div className="mt-4 sm:mt-6">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-xs sm:text-sm">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="p-2 sm:p-3 border font-semibold">Service Name</th>
                        <th className="p-2 sm:p-3 border font-semibold">Quantity</th>
                        <th className="p-2 sm:p-3 border font-semibold text-right">Unit Price (৳)</th>
                        <th className="p-2 sm:p-3 border font-semibold text-right">Amount (৳)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {serviceList.map((service, index) => (
                        <tr key={service.id}>
                          <td className="p-2 sm:p-3 border text-center">{service.name}</td>
                          <td className="p-2 sm:p-3 border text-center">{service.quantity}</td>
                          <td className="p-2 sm:p-3 border text-right">{parseFloat(service.price).toFixed(2)}</td>
                          <td className="p-2 sm:p-3 border text-right">{parseFloat(service.amount).toFixed(2)}</td>
                        </tr>
                      ))}
                      {/* Calculation Area */}
                      <tr>
                        <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3 pt-4" colSpan="3">
                          <p><strong>Sub Total (৳) :</strong></p>
                        </td>
                        <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3">
                          <p>{subTotal.toFixed(2)}</p>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3" colSpan="3">
                          <p><strong>Discount (৳) :</strong></p>
                        </td>
                        <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3">
                          <p>{parseFloat(discount || 0).toFixed(2)}</p>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-end capitalize font-inter font-normal text-sm text-gray-800" colSpan="3">
                          <strong className="py-1 px-3 text-sm">Net Total (৳) :</strong>
                        </td>
                        <td className="text-end capitalize font-inter font-normal text-sm text-gray-800">
                          <strong className="py-1 px-3 border-t border-gray-400/20 block">{parseFloat(netAmount || 0).toFixed(2)}</strong>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3" colSpan="3">
                          <p><strong>Paid Amount (৳) :</strong></p>
                        </td>
                        <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3">
                          <p>{parseFloat(paidAmount || 0).toFixed(2)}</p>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3" colSpan="3">
                          <p><strong>Due Amount (৳) :</strong></p>
                        </td>
                        <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3">
                          <p>{dueAmount}</p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Print Layout (A5) */}
      <PrintBillLayoutA5
        className="print-layout"
        billInfo={billInfoContent}
        table={tableContent}
        summary={summaryContent}
        title="Hospital Bill"
        footer={signatureFooter}
      />

      {/* Edit Modal */}
      <HospitalBillModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          onClose();
        }}
        onSave={(newBill, isPrintAction = false) => {
          onEdit(newBill);
        }}
        billId={id}
      />
    </>
  );
};

export default HospitalViewBillModal;